import { useId, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { cx } from '@/lib/clases'
import { formatearFecha } from '@/lib/fecha'
import type { EstadoCorte } from '@/lib/fecha'
import { Boton } from '@/ui/Boton'
import { Banner } from '@/ui/Banner'
import { CampoTexto } from '@/ui/CampoFormulario'
import { EstadoError } from '@/ui/EstadoError'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { Esqueleto, LineaEsqueleto } from '@/ui/Esqueleto'
import { InsigniaEstado, PuntoEstado } from '@/ui/InsigniaEstado'
import { ENCF, FechaCorta, Monto, RNC } from '@/ui/Numero'
import { PanelLateral } from '@/ui/PanelLateral'
import type { Tono } from '@/ui/tonos'
import { traducirErrorApi } from '@/dominio/documentos/errores'
import { nombreTipoECF } from '@/dominio/documentos/tipos'
import {
  TEXTO_ACCION_ENVIO,
  conCorte,
  presentacionEnvioDgii,
  presentacionEnvioEmisor,
  textoFirmaAcecf,
  type EnvioAprobacion,
  type GrupoDeCorte,
  type PresentacionEnvio,
  type RecibidoConCorte,
} from '@/dominio/recibidos/tipos'
import {
  useAprobarRecibidos,
  useBandejaDeRecibidos,
  useRecibido,
} from '@/dominio/recibidos/useRecibidos'
import { EncabezadoPantalla, Tarjeta } from './-pantalla'

/**
 * Recibidos y aprobación comercial (E29, E30, E31).
 *
 * **El eje es el corte mensual, no la antigüedad.** El receptor manifiesta
 * conformidad mediante la aprobación comercial **o** mediante el 606, a más
 * tardar el día 15 del mes siguiente a la fecha de la factura. Y el corte no es
 * uno: cada documento tiene el suyo, derivado de su fecha, así que la pantalla
 * agrupa por corte.
 *
 * Dos cosas que no se tocan:
 *   **el acuse de recibo (ARECF) es obligatorio y se genera solo.** La interfaz
 *   solo lo informa en el detalle. No hay botón para eso y no debe haberlo
 *
 *   **el grupo "fuera de ventana" no se esconde ni se borra.** Va al final, en
 *   gris, con la salida real: declararlo en el 606
 *
 * Y una tercera, desde que la respuesta comercial se manda de verdad: **el
 * ACECF va a dos destinatarios, el emisor y DGII, y el detalle pinta cada
 * desenlace por separado.** Registrar «aprobado» no es que el proveedor se haya
 * enterado.
 */
/**
 * Qué parte de la bandeja se mira. Sin `vista`, lo pendiente, que es lo que se
 * trabaja aquí.
 *
 * **Las respondidas tienen que poder verse**, y hasta ahora no podían: en cuanto
 * se aprobaba algo desaparecía de la lista, y con ello la única forma de saber si
 * el ACECF le llegó al emisor y a DGII. Aprobar y mandar son dos cosas, y la
 * segunda se revisa después.
 */
export type VistaRecibidos = 'aprobados' | 'rechazados'

export interface BusquedaRecibidos {
  texto?: string
  vista?: VistaRecibidos
}

export const Route = createFileRoute('/recibidos')({
  validateSearch: (busqueda: Record<string, unknown>): BusquedaRecibidos => ({
    texto: typeof busqueda.texto === 'string' ? busqueda.texto : undefined,
    vista:
      busqueda.vista === 'aprobados' || busqueda.vista === 'rechazados' ? busqueda.vista : undefined,
  }),
  component: PantallaRecibidos,
})

/** El motivo viaja en el ACECF y es lo único con lo que el emisor puede corregir. */
const MOTIVO_MINIMO = 5

/** Verde sobre 10 días, ámbar bajo 5, rojo el 14 y el 15, gris fuera. */
const TONO_CORTE: Record<EstadoCorte, Tono> = {
  holgado: 'verde',
  proximo: 'ambar',
  critico: 'rojo',
  'fuera-de-ventana': 'neutral',
}

function PantallaRecibidos() {
  const busqueda = Route.useSearch()
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set())
  const [rechazando, setRechazando] = useState(false)
  const [detalle, setDetalle] = useState<RecibidoConCorte | null>(null)

  // Una vista por estado, y el filtro lo aplica el servidor: pedir la bandeja
  // entera para separar en el navegador gasta páginas en filas que no se enseñan.
  const vista = busqueda.vista
  const pendientes = vista === undefined
  const { consulta, bandeja } = useBandejaDeRecibidos({
    texto: busqueda.texto,
    aprobacion: vista === 'aprobados' ? 'aprobado' : vista === 'rechazados' ? 'rechazado' : 'pendiente',
  })
  const aprobar = useAprobarRecibidos()

  const alternar = (id: string): void => {
    setSeleccion((actual) => {
      const siguiente = new Set(actual)
      if (siguiente.has(id)) siguiente.delete(id)
      else siguiente.add(id)
      return siguiente
    })
  }

  // Lo que se respondió desde el panel sale de la selección en lote. Si se
  // quedara, el siguiente «Aprobar seleccionados» le mandaría un segundo ACECF
  // —quizá el contrario— a un comprobante que ya ni se ve en la bandeja.
  const quitarDeLaSeleccion = (id: string): void => {
    setSeleccion((actual) => {
      if (!actual.has(id)) return actual
      const siguiente = new Set(actual)
      siguiente.delete(id)
      return siguiente
    })
  }

  const errorTraducido = consulta.isError ? traducirErrorApi(consulta.error) : null

  if (errorTraducido !== null) {
    return (
      <div className="flex flex-col gap-4">
        <EncabezadoPantalla titulo="Recibidos" />
        <EstadoError
          titulo={errorTraducido.titulo}
          contexto={errorTraducido.contexto}
          detalleTecnico={errorTraducido.detalleTecnico}
          onReintentar={() => void consulta.refetch()}
        />
      </div>
    )
  }

  return (
    <div className="flex max-w-[1100px] flex-col gap-4">
      <EncabezadoPantalla
        titulo={
          vista === 'aprobados'
            ? 'Aprobados'
            : vista === 'rechazados'
              ? 'Rechazados'
              : 'Aprobaciones pendientes'
        }
        descripcion={
          // «Detenido» solo tiene sentido en lo pendiente: lo respondido ya no
          // espera a nadie.
          consulta.isLoading || !pendientes ? undefined : (
            // Esta línea es el argumento de venta de todo el módulo, y el
            // argumento correcto no es el de cumplimiento: es "esta plata está
            // detenida y tienes hasta el 15".
            <span className="flex flex-wrap items-baseline gap-2 text-cuerpo">
              <Monto valor={bandeja.montoDetenido} mostrarMoneda className="text-gris-900" />
              <span className="text-gris-600">detenidos</span>
              {bandeja.diasAlCorteMasCercano !== null ? (
                <>
                  <span className="text-gris-400">·</span>
                  <span className="text-gris-600">
                    el corte del 15 es en{' '}
                    <span className="numero text-gris-900">{bandeja.diasAlCorteMasCercano}</span> días
                  </span>
                </>
              ) : null}
            </span>
          )
        }
      />

      <nav aria-label="Vistas de la bandeja" className="flex gap-1 border-b border-gris-200">
        {(
          [
            [undefined, 'Pendientes'],
            ['aprobados', 'Aprobados'],
            ['rechazados', 'Rechazados'],
          ] as const
        ).map(([destino, etiqueta]) => (
          <Link
            key={etiqueta}
            to="/recibidos"
            search={{ texto: busqueda.texto, vista: destino }}
            className={cx(
              '-mb-px border-b-2 px-3 py-1.5 text-cuerpo',
              vista === destino
                ? 'border-marca text-gris-900'
                : 'border-transparent text-gris-600 hover:text-gris-900',
            )}
          >
            {etiqueta}
          </Link>
        ))}
      </nav>

      {consulta.isLoading ? (
        <div className="flex flex-col gap-3">
          <Esqueleto className="h-9 w-full" />
          <Esqueleto className="h-9 w-full" />
          <Esqueleto className="h-9 w-5/6" />
        </div>
      ) : bandeja.grupos.length === 0 ? (
        // Todo resuelto es una buena noticia y debe ocupar poco espacio.
        <Tarjeta relleno={false}>
          <EstadoVacio
            variante="resuelto"
            mensaje={
              vista === 'aprobados'
                ? 'Todavía no hay recibidos aprobados.'
                : vista === 'rechazados'
                  ? 'Todavía no hay recibidos rechazados.'
                  : 'Sin aprobaciones pendientes.'
            }
          />
        </Tarjeta>
      ) : (
        <div className="flex flex-col gap-4">
          {bandeja.grupos.map((grupo) => (
            <GrupoDeAprobaciones
              key={grupo.corte}
              grupo={grupo}
              seleccion={seleccion}
              seleccionable={pendientes}
              onAlternar={alternar}
              onAbrir={setDetalle}
            />
          ))}
        </div>
      )}

      {/* Barra de acciones en lote ------------------------------------- */}
      {seleccion.size > 0 ? (
        <div className="sticky bottom-0 flex flex-wrap items-center gap-2 rounded-md border border-gris-300 bg-white px-3 py-2 shadow-flotante">
          <span className="text-cuerpo text-gris-900">
            <span className="numero">{seleccion.size}</span> seleccionadas
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Boton
              variante="primario"
              disabled={aprobar.isPending}
              onClick={() =>
                aprobar.mutate(
                  { ids: [...seleccion], aprobado: true },
                  { onSuccess: () => setSeleccion(new Set()) },
                )
              }
            >
              Aprobar seleccionados
            </Boton>
            <Boton variante="secundario" onClick={() => setRechazando(true)}>
              Rechazar con motivo
            </Boton>
            <Link to="/reportes" search={{ reporte: '606' }}>
              <Boton variante="discreto">Ver en el 606</Boton>
            </Link>
          </div>
        </div>
      ) : null}

      <PanelDeRechazo
        abierto={rechazando}
        cantidad={seleccion.size}
        trabajando={aprobar.isPending}
        onCerrar={() => setRechazando(false)}
        onConfirmar={(motivo) =>
          aprobar.mutate(
            { ids: [...seleccion], aprobado: false, motivo },
            {
              onSuccess: () => {
                setSeleccion(new Set())
                setRechazando(false)
              },
            },
          )
        }
      />

      {/* La clave reinicia el panel al cambiar de comprobante: un motivo de
          rechazo a medio escribir no puede sobrevivir y acabar en el ACECF de
          otra factura. */}
      <PanelDeRecibido
        key={detalle?.id ?? 'cerrado'}
        recibido={detalle}
        onCerrar={() => setDetalle(null)}
        onRespondido={quitarDeLaSeleccion}
      />
    </div>
  )
}

/* ---------------------------------------------------------------------- */

function GrupoDeAprobaciones({
  grupo,
  seleccion,
  seleccionable,
  onAlternar,
  onAbrir,
}: {
  grupo: GrupoDeCorte
  seleccion: Set<string>
  /**
   * Solo en lo pendiente. Seleccionar para aprobar en lote algo ya respondido
   * mandaría un segundo ACECF —quizá el contrario— sobre la misma factura.
   */
  seleccionable: boolean
  onAlternar: (id: string) => void
  onAbrir: (recibido: RecibidoConCorte) => void
}) {
  const titulo = grupo.fueraDeVentana
    ? `Fuera de ventana · el corte del ${formatearFecha(grupo.corte)} ya pasó`
    : `Corte ${formatearFecha(grupo.corte)}`

  return (
    <section
      className={cx(
        'rounded-md border border-gris-300 bg-white',
        // Gris para el grupo vencido: sigue ahí, pero no compite por atención.
        grupo.fueraDeVentana && 'opacity-90',
      )}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gris-300 px-3 py-2">
        <h2 className="flex items-center gap-2 text-seccion text-gris-900">
          <PuntoEstado tono={TONO_CORTE[grupo.semaforo]} />
          {titulo}
        </h2>
        <p className="flex items-baseline gap-3 text-etiqueta text-gris-600">
          <Monto valor={grupo.monto} mostrarMoneda className="text-gris-900" />
          {!grupo.fueraDeVentana && grupo.diasRestantes !== null ? (
            <span>
              <span className="numero text-gris-900">{grupo.diasRestantes}</span> días
            </span>
          ) : null}
          <span>
            <span className="numero text-gris-900">{grupo.documentos.length}</span> docs
          </span>
        </p>
      </header>

      {grupo.fueraDeVentana ? (
        <p className="border-b border-gris-200 bg-gris-50 px-3 py-1 text-etiqueta text-gris-600">
          Ya no aplica la aprobación: declárala en el 606.
        </p>
      ) : null}

      <table className="w-full table-fixed border-collapse text-cuerpo" data-densidad="compacta">
        <tbody>
          {grupo.documentos.map((documento) => (
            <tr
              key={documento.id}
              className="h-[var(--alto-fila)] border-b border-gris-200 last:border-b-0 hover:bg-gris-50"
            >
              <td className="w-10 px-2">
                {seleccionable ? (
                  <input
                    type="checkbox"
                    aria-label={`Seleccionar ${documento.eNCF}`}
                    checked={seleccion.has(documento.id)}
                    disabled={!documento.admiteAprobacion}
                    onChange={() => onAlternar(documento.id)}
                    className="size-4 accent-[var(--color-marca)]"
                  />
                ) : null}
              </td>
              <td className="w-[150px] overflow-hidden px-2 text-right">
                <ENCF valor={documento.eNCF} abreviado />
              </td>
              <td className="overflow-hidden px-2">
                <button
                  type="button"
                  onClick={() => onAbrir(documento)}
                  className="block w-full truncate text-left text-gris-900 hover:text-marca"
                  title={documento.emisorNombre ?? undefined}
                >
                  {/* El emisor puede llegar sin razon social: el esquema la tiene
                      nullable porque un e-CF de un tercero puede venir sin ella y
                      registrarlo igual es lo que sostiene la bandeja y el 606. Se
                      muestra el RNC, que siempre esta, en vez de un hueco. */}
                  {documento.emisorNombre ?? documento.emisorRNC}
                </button>
              </td>
              <td className="w-[130px] px-2 text-right">
                <FechaCorta valor={documento.fecha} />
              </td>
              <td className="w-[150px] px-2 text-right">
                <Monto valor={documento.total} moneda={documento.moneda} />
              </td>
              <td className="w-[170px] px-2">
                {documento.admiteAprobacion ? (
                  <span className="text-etiqueta text-gris-600">
                    {documento.diasRestantes >= 0
                      ? `${documento.diasRestantes} días`
                      : 'vencido'}
                  </span>
                ) : (
                  // Los tipos 32, 41, 43, 46 y 47 no admiten aprobación
                  // comercial: DGII rechaza la operación. Ofrecerla sería
                  // generarle un error al usuario.
                  <span className="text-etiqueta text-gris-600">no admite aprobación</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

/** El rechazo **exige motivo, siempre**, y el motivo viaja en el ACECF. */
function PanelDeRechazo({
  abierto,
  cantidad,
  trabajando,
  onCerrar,
  onConfirmar,
}: {
  abierto: boolean
  cantidad: number
  trabajando: boolean
  onCerrar: () => void
  onConfirmar: (motivo: string) => void
}) {
  const [motivo, setMotivo] = useState('')

  return (
    <PanelLateral
      abierto={abierto}
      onCerrar={onCerrar}
      ancho="estrecho"
      titulo="Rechazar comercialmente"
      subtitulo={`${cantidad} comprobante${cantidad === 1 ? '' : 's'}`}
      pie={
        <>
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            variante="destructivo"
            disabled={motivo.trim().length < MOTIVO_MINIMO || trabajando}
            onClick={() => onConfirmar(motivo.trim())}
          >
            {trabajando ? 'Enviando…' : 'Rechazar'}
          </Boton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <CampoTexto
          etiqueta="Motivo"
          obligatorio
          value={motivo}
          onChange={(evento) => setMotivo(evento.target.value)}
          ayuda="Viaja en el ACECF hacia el emisor. Concreto y verificable."
        />
        <p className="text-cuerpo text-gris-600">
          Rechazar un comprobante que no reconoces evita declarar una compra que vas a tener que
          pelear después.
        </p>
      </div>
    </PanelLateral>
  )
}

/**
 * El detalle de un recibido, y el sitio donde se le responde.
 *
 * **Se responde aquí y no solo en lote** porque aquí es donde se ve lo que pasó
 * después: la bandeja enseña solo lo pendiente, así que un comprobante
 * respondido desaparece de ella, y su envío —a DGII y al emisor— solo se puede
 * seguir en un panel que siga abierto.
 */
function PanelDeRecibido({
  recibido,
  onCerrar,
  onRespondido,
}: {
  recibido: RecibidoConCorte | null
  onCerrar: () => void
  /** Este comprobante ya tiene respuesta: se saca de la selección en lote. */
  onRespondido: (id: string) => void
}) {
  const consulta = useRecibido(recibido?.id ?? null)
  const responder = useAprobarRecibidos()
  const [rechazando, setRechazando] = useState(false)
  const [motivo, setMotivo] = useState('')

  // La fila de la bandeja pinta el panel al instante, y en cuanto llega el
  // detalle manda él. La fila es una foto de cuando se cargó la bandeja: tras
  // responder seguiría diciendo «pendiente» y no traería el envío.
  const actual = consulta.data !== undefined ? conCorte(consulta.data.resumen) : recibido
  const envio = consulta.data?.envioAprobacion ?? null
  // `isSuccess` cierra el hueco entre que la respuesta se registra y llega el
  // detalle refrescado: sin él los botones reaparecen un instante sobre un
  // comprobante que todavía dice «pendiente», y un segundo clic manda otra.
  const puedeResponder =
    !responder.isSuccess &&
    actual !== null &&
    actual.aprobacion === 'pendiente' &&
    actual.admiteAprobacion

  const enviar = (aprobado: boolean): void => {
    if (actual === null) return
    const id = actual.id
    responder.mutate(
      { ids: [id], aprobado, motivo: aprobado ? undefined : motivo.trim() },
      {
        onSuccess: () => {
          setRechazando(false)
          setMotivo('')
          onRespondido(id)
        },
      },
    )
  }

  const errorDetalle = consulta.isError ? traducirErrorApi(consulta.error) : null
  const errorRespuesta = responder.isError ? traducirErrorApi(responder.error) : null

  return (
    <PanelLateral
      abierto={recibido !== null}
      onCerrar={onCerrar}
      titulo={actual?.emisorNombre ?? 'Comprobante recibido'}
      subtitulo={actual !== null ? <ENCF valor={actual.eNCF} /> : undefined}
      pie={
        !puedeResponder ? undefined : rechazando ? (
          <>
            <Boton variante="secundario" onClick={() => setRechazando(false)}>
              Cancelar
            </Boton>
            <Boton
              variante="destructivo"
              disabled={motivo.trim().length < MOTIVO_MINIMO || responder.isPending}
              onClick={() => enviar(false)}
            >
              {responder.isPending ? 'Enviando…' : 'Rechazar'}
            </Boton>
          </>
        ) : (
          <>
            <Boton
              variante="secundario"
              disabled={responder.isPending}
              onClick={() => setRechazando(true)}
            >
              Rechazar con motivo
            </Boton>
            <Boton variante="primario" disabled={responder.isPending} onClick={() => enviar(true)}>
              {responder.isPending ? 'Enviando…' : 'Aprobar'}
            </Boton>
          </>
        )
      }
    >
      {actual === null ? (
        <LineaEsqueleto />
      ) : (
        <div className="flex flex-col gap-4">
          <dl className="flex flex-col gap-1">
            <div className="flex justify-between border-b border-gris-200 py-1">
              <dt className="text-etiqueta text-gris-600">Tipo</dt>
              <dd className="text-cuerpo text-gris-900">{nombreTipoECF(actual.tipo)}</dd>
            </div>
            <div className="flex justify-between border-b border-gris-200 py-1">
              <dt className="text-etiqueta text-gris-600">RNC del emisor</dt>
              <dd>
                <RNC valor={actual.emisorRNC} />
              </dd>
            </div>
            <div className="flex justify-between border-b border-gris-200 py-1">
              <dt className="text-etiqueta text-gris-600">Fecha de la factura</dt>
              <dd>
                <FechaCorta valor={actual.fecha} />
              </dd>
            </div>
            <div className="flex justify-between border-b border-gris-200 py-1">
              <dt className="text-etiqueta text-gris-600">Corte de conformidad</dt>
              <dd className="flex items-center gap-2">
                <PuntoEstado tono={TONO_CORTE[actual.semaforo]} />
                <FechaCorta valor={actual.corte} />
              </dd>
            </div>
            <div className="flex justify-between border-b border-gris-200 py-1">
              <dt className="text-etiqueta text-gris-600">Total</dt>
              <dd>
                <Monto valor={actual.total} moneda={actual.moneda} mostrarMoneda />
              </dd>
            </div>
          </dl>

          {/* Sin el detalle el panel sigue sirviendo con la fila de la bandeja,
              pero el envío no se puede enseñar, y hay que decirlo. */}
          {errorDetalle !== null ? (
            <Banner
              tono="ambar"
              titulo="No se pudo leer el detalle del comprobante."
              detalleTecnico={errorDetalle.detalleTecnico}
              accion={
                <Boton variante="secundario" onClick={() => void consulta.refetch()}>
                  Reintentar
                </Boton>
              }
            >
              Lo de arriba viene de la bandeja. El estado del envío de la respuesta no está
              disponible hasta que el detalle cargue.
            </Banner>
          ) : null}

          {/*
            El acuse es técnico y automático: no implica conformidad con el
            contenido. Se informa, y no hay botón, a propósito.
          */}
          <Banner
            tono="azul"
            titulo={actual.acuseEnviado ? 'Acuse de recibo enviado' : 'Acuse de recibo en curso'}
          >
            El ARECF es obligatorio y se genera solo. Es técnico: no implica conformidad con el
            contenido. Lo opcional es la aprobación comercial, que es lo que decides aquí.
          </Banner>

          {actual.aprobacion === 'aprobado' ? (
            <InsigniaEstado tono="verde">Aprobado comercialmente</InsigniaEstado>
          ) : actual.aprobacion === 'rechazado' ? (
            <div className="flex flex-col gap-1">
              <InsigniaEstado tono="rojo">Rechazado comercialmente</InsigniaEstado>
              {actual.motivoRechazo !== null ? (
                <p className="text-etiqueta text-gris-600">{actual.motivoRechazo}</p>
              ) : null}
            </div>
          ) : actual.admiteAprobacion ? (
            <InsigniaEstado tono="ambar" aclaracion="opcional">
              Pendiente de aprobación
            </InsigniaEstado>
          ) : (
            <InsigniaEstado tono="neutral">Este tipo no admite aprobación comercial</InsigniaEstado>
          )}

          {puedeResponder && rechazando ? (
            <CampoTexto
              etiqueta="Motivo del rechazo"
              obligatorio
              // El foco va al campo porque el usuario acaba de pedir rechazar:
              // lo siguiente que va a hacer es escribir el motivo.
              autoFocus
              value={motivo}
              onChange={(evento) => setMotivo(evento.target.value)}
              ayuda="Viaja en el ACECF hacia el emisor. Concreto y verificable."
            />
          ) : null}

          {errorRespuesta !== null ? (
            <Banner
              tono={errorRespuesta.tono}
              titulo={errorRespuesta.titulo}
              detalleTecnico={errorRespuesta.detalleTecnico}
            >
              {errorRespuesta.contexto}
            </Banner>
          ) : null}

          {envio !== null ? <EnvioDeLaRespuesta envio={envio} /> : null}
        </div>
      )}
    </PanelLateral>
  )
}

/**
 * El envío de la respuesta comercial: al emisor de la factura, y una copia a
 * DGII.
 *
 * **Son dos desenlaces y se pintan por separado.** DGII puede haber aceptado y
 * el emisor no, o al revés, y cada uno se arregla en un sitio distinto. Un
 * estado único «enviado» escondería justo el caso que hay que ver.
 *
 * `aria-live` porque el paso de «en cola» a su desenlace llega por el sondeo,
 * sin que el usuario toque nada, y tiene que oírse igual que se ve.
 */
function EnvioDeLaRespuesta({ envio }: { envio: EnvioAprobacion }) {
  const idTitulo = useId()

  return (
    <section aria-labelledby={idTitulo} aria-live="polite" className="flex flex-col gap-2">
      <header className="flex flex-col gap-1">
        <h3 id={idTitulo} className="text-seccion text-gris-900">
          Envío de la respuesta
        </h3>
        <p className="text-etiqueta text-gris-600">
          El ACECF va al emisor de la factura y una copia a DGII. Cada uno contesta por su lado.{' '}
          {textoFirmaAcecf(envio.firmada)}
        </p>
      </header>

      <DestinoDelEnvio
        destinatario="DGII · copia"
        presentacion={presentacionEnvioDgii(envio.dgii)}
        mensaje={envio.dgiiMensaje}
      />
      <DestinoDelEnvio
        destinatario="Emisor de la factura"
        presentacion={presentacionEnvioEmisor(envio.emisor)}
        mensaje={envio.emisorMensaje}
      />
    </section>
  )
}

function DestinoDelEnvio({
  destinatario,
  presentacion,
  mensaje,
}: {
  destinatario: string
  presentacion: PresentacionEnvio
  mensaje: string | null
}) {
  return (
    <div className="flex flex-col gap-1 border-t border-gris-200 pt-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-etiqueta text-gris-600">{destinatario}</span>
        <InsigniaEstado tono={presentacion.tono} aclaracion={presentacion.aclaracion}>
          {presentacion.etiqueta}
        </InsigniaEstado>
      </div>

      <p className="text-etiqueta text-gris-600">{presentacion.explicacion}</p>

      {/* Qué hacer, en el mismo bloque. El rechazo de DGII y el del emisor se
          arreglan en sitios opuestos, y un estado sin salida obliga a
          deducirla. «Nada que hacer» sobre un verde es ruido y se calla. */}
      {presentacion.accion !== 'ninguna' ? (
        <p className="text-etiqueta text-gris-900">{TEXTO_ACCION_ENVIO[presentacion.accion]}</p>
      ) : null}

      {mensaje !== null ? (
        <p className="text-etiqueta text-gris-600">Respuesta: {mensaje}</p>
      ) : null}
    </div>
  )
}
